import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

import { RecognizeMercanaVipBackfill } from "./recognize-mercana-vip-backfill";

interface RecognizeOrdersCardProps {
  teamId: string;
  enabled: boolean;
}

export const RecognizeOrdersCard = memo(function RecognizeOrdersCard({
  teamId,
  enabled,
}: RecognizeOrdersCardProps) {
  const client = useRequiredContext(RedoAdminRpcClientContext);
  const [recentCount, setRecentCount] = useState("2000");
  const [returnerCount, setReturnerCount] = useState("");
  const status = useRpcQuery(RedoAdminRpcClientContext, {
    method: "getRecognizeOrdersStatus",
    args: { teamId },
    options: {
      enabled,
      refetchInterval: (query) => {
        if (query.state.data?.status === "RUNNING") {
          return 5000;
        }
        return false;
      },
    },
  });
  const [run, start] = useTriggerLoadWithInput(
    async ({
      targetCount,
      returnersOnly,
    }: {
      targetCount: number;
      returnersOnly: boolean;
    }) => {
      let scope = `enough orders to reach ${targetCount.toLocaleString()} unique successfully enriched profiles`;
      if (returnersOnly) {
        scope = `up to ${targetCount.toLocaleString()} customers with at least one processed return`;
      }
      const confirmed = window.confirm(
        `Run Recognize on ${scope} for this store? Eligible research will be reused. This does not run Recognize Actions or OMS automations.`,
      );
      if (!confirmed) {
        return;
      }
      const input = { teamId, targetCount };
      const result = returnersOnly
        ? await client.runRecognizeReturners(input)
        : await client.runRecognizeOrders(input);
      await status.refetch();
      if (!result.started) {
        toast(result.message, { variant: "error" });
        return;
      }
      toast("Recognize started in the background.", { variant: "success" });
    },
  );
  const count = Number(recentCount);
  const invalidCount =
    !Number.isSafeInteger(count) || count < 1 || count > 10_000;
  const isRunning = status.data?.status === "RUNNING";
  const disabled =
    !enabled || status.isPending || status.isError || run.pending || isRunning;
  const returnerTarget = Number(returnerCount);
  const cannotBackfillReturners =
    disabled ||
    !Number.isSafeInteger(returnerTarget) ||
    returnerTarget < 1 ||
    returnerTarget > 10_000;
  const startReturnerBackfill = () => {
    if (!cannotBackfillReturners) {
      void start({ targetCount: returnerTarget, returnersOnly: true });
    }
  };
  let runningLabel = "Running…";
  if (isRunning && status.data) {
    const { completed, failed, skipped, selected } = status.data.progress;
    const processed = (completed + failed + skipped).toLocaleString();
    runningLabel = `Running · ${processed} processed`;
    if (selected !== null) {
      runningLabel = `Running · ${processed} of ${selected.toLocaleString()} processed`;
      if (status.data.progress.mode === "fill") {
        runningLabel = `Running · ${completed.toLocaleString()} of ${selected.toLocaleString()} profiles enriched`;
      }
    }
  }

  return (
    <Card title="Recognize existing orders">
      <Flex dir="column" gap="lg" p="lg">
        <Text fontSize="sm" textColor="secondary">
          Research customers from existing outbound orders using the store’s
          Recognize source filters. Eligible research is reused. This does not
          run Recognize Actions or OMS automations.
        </Text>
        {!enabled && (
          <Text fontSize="sm" textColor="secondary">
            Enable Recognize for this store before running.
          </Text>
        )}
        <Flex align="flex-start" dir="column" gap="md">
          <RedoTextInput
            inputTypeHint="number"
            label="Enriched profile target"
            max={10_000}
            min={1}
            setValue={setRecentCount}
            step={1}
            value={recentCount}
          />
          <Flex align="center" gap="md" wrap="wrap">
            <RedoButton
              disabled={disabled || invalidCount}
              hierarchy="secondary"
              onClick={() =>
                void start({ targetCount: count, returnersOnly: false })
              }
              size="sm"
              text="Run up to"
            />
            {isRunning && (
              <Text fontSize="sm" textColor="secondary">
                {runningLabel}
              </Text>
            )}
          </Flex>
        </Flex>
        <Divider />
        <Text fontSize="sm" textColor="secondary">
          Run up to counts unique successfully enriched profiles across previous
          runs and continues to older orders after skips or failures. Rerunning
          fills only the remaining gap.
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Mercana runs submit customers in batches of up to 500 and allow up to
          2,000 unfinished customers across stores. Source and country filters
          apply.
        </Text>
        <Divider />
        <Flex align="flex-start" dir="column" gap="md">
          <Text fontSize="sm" textColor="secondary">
            Enrich customers with at least one processed return using Mercana.
            The run continues until it reaches the target or runs out of
            eligible customers.
          </Text>
          <RedoTextInput
            inputTypeHint="number"
            label="Returning customer target"
            max={10_000}
            min={1}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                startReturnerBackfill();
              }
            }}
            placeholder="300"
            setValue={setReturnerCount}
            step={1}
            value={returnerCount}
          />
          <RedoButton
            disabled={cannotBackfillReturners}
            hierarchy="secondary"
            onClick={startReturnerBackfill}
            size="sm"
            text="Backfill customers with returns"
          />
        </Flex>
        <Divider />
        <RecognizeMercanaVipBackfill enabled={enabled} teamId={teamId} />
        {status.error && (
          <Text fontSize="sm" textColor="error">
            Unable to load run status: {status.error.message}
          </Text>
        )}
        {run.error && (
          <Text fontSize="sm" textColor="error">
            Unable to start research: {run.error.message}
          </Text>
        )}
      </Flex>
    </Card>
  );
});
